"use client";

import { c as _c } from "react/compiler-runtime";
import { CardItem, CardItemButton, CardItemLabel, CardItemValue } from "@/components/ui/Card/Card";
import type { CardDivProps } from "@/components/ui/Card/Card";
import { Skeleton } from "@/components/ui/Skeleton";
import { ChevronDown } from "lucide-react";
import Link from "next/link";
import { Children, useState } from "react";
import type { ReactNode } from "react";
import cn from "twsx";
const metricFormatter = new Intl.NumberFormat("en-US");
export type CardMetricTone = "strong" | "quiet" | "muted" | "attention" | "critical";

// Empty buckets always read calm — a zero is rarely the thing you came to act
// on — so they collapse to a chrome-less, faint "0" regardless of tone. This
// lets the rows that actually have values carry all the visual weight.
const emptyCount = "border-transparent bg-transparent text-text-quaternary";
const countToneStyles: Record<CardMetricTone, {
  active: string;
  empty: string;
}> = {
  // Group headers and primary lines: dark inverse fill when there is work.
  strong: {
    active: "border-transparent bg-text-primary text-text-on-accent",
    empty: emptyCount
  },
  // Breakdown rows: a bordered surface pill when populated.
  quiet: {
    active: "border-border-primary bg-bg-surface text-text-secondary",
    empty: emptyCount
  },
  // Terminal and reference counts stay visible without competing.
  muted: {
    active: emptyCount,
    empty: emptyCount
  },
  // Work queues where a non-zero value means something to clear.
  attention: {
    active: "border-transparent bg-bg-secondary text-yellow-strong",
    empty: emptyCount
  },
  // Overdue or urgent work.
  critical: {
    active: "border-transparent bg-red-bg-subtle text-red-text",
    empty: emptyCount
  }
};
export function CardMetricCount(t0) {
  const $ = _c(8);
  const {
    value,
    tone: t1,
    isLoading: t2
  } = t0;
  const tone = t1 === undefined ? "strong" : t1;
  const isLoading = t2 === undefined ? false : t2;
  if (isLoading) {
    let t3;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <Skeleton size="md" shape="pill" className="w-10 justify-self-end" />;
      $[0] = t3;
    } else {
      t3 = $[0];
    }
    return t3;
  }
  const count = value ?? 0;
  const t3 = count > 0 ? countToneStyles[tone].active : countToneStyles[tone].empty;
  let t4;
  if ($[1] !== t3) {
    t4 = cn("inline-flex h-6 min-w-7 shrink-0 items-center justify-center justify-self-end", "rounded-full border px-2 text-xs font-medium tabular-nums", t3);
    $[1] = t3;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== count) {
    t5 = metricFormatter.format(count);
    $[3] = count;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== t4 || $[6] !== t5) {
    t6 = <div className={t4}>{t5}</div>;
    $[5] = t4;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  return t6;
}
type CardMetricLineValueTone = "default" | "quiet" | "attention" | "critical";
const valueToneToCountTone: Record<CardMetricLineValueTone, CardMetricTone> = {
  default: "strong",
  quiet: "quiet",
  attention: "attention",
  critical: "critical"
};
type CardMetricLineProps = CardDivProps & {
  href?: string;
  isLoading?: boolean;
  label: string;
  valueTone?: CardMetricLineValueTone;
  value: number | undefined;
};
export function CardMetricLine(t0) {
  const $ = _c(32);
  let className;
  let href;
  let label;
  let props;
  let ref;
  let t1;
  let t2;
  let value;
  if ($[0] !== t0) {
    ({
      className,
      label,
      value,
      valueTone: t1,
      isLoading: t2,
      href,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = href;
    $[3] = label;
    $[4] = props;
    $[5] = ref;
    $[6] = t1;
    $[7] = t2;
    $[8] = value;
  } else {
    className = $[1];
    href = $[2];
    label = $[3];
    props = $[4];
    ref = $[5];
    t1 = $[6];
    t2 = $[7];
    value = $[8];
  }
  const valueTone = t1 === undefined ? "default" : t1;
  const isLoading = t2 === undefined ? false : t2;
  const t3 = (value ?? 0) > 0 ? "muted" : "faint";
  let t4;
  if ($[9] !== label || $[10] !== t3) {
    t4 = <CardItemLabel tone={t3} className="min-w-0 flex-1 truncate">{label}</CardItemLabel>;
    $[9] = label;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const t5 = valueToneToCountTone[valueTone];
  let t6;
  if ($[12] !== isLoading || $[13] !== t5 || $[14] !== value) {
    t6 = <CardItemValue><CardMetricCount value={value} tone={t5} isLoading={isLoading} /></CardItemValue>;
    $[12] = isLoading;
    $[13] = t5;
    $[14] = value;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t4 || $[17] !== t6) {
    t7 = <CardItem size="md" className="flex-nowrap">{t4}{t6}</CardItem>;
    $[16] = t4;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const content = t7;
  if (href) {
    let t8;
    if ($[19] !== className) {
      t8 = cn("flex w-full flex-row justify-between rounded-none hover:bg-fill-hover-subtle", className);
      $[19] = className;
      $[20] = t8;
    } else {
      t8 = $[20];
    }
    let t9;
    if ($[21] !== content || $[22] !== href || $[23] !== t8) {
      t9 = <Link href={href} prefetch={false} className={t8}>{content}</Link>;
      $[21] = content;
      $[22] = href;
      $[23] = t8;
      $[24] = t9;
    } else {
      t9 = $[24];
    }
    return t9;
  }
  let t8;
  if ($[25] !== className) {
    t8 = cn("flex w-full flex-row justify-between rounded-none hover:bg-fill-hover-subtle", className);
    $[25] = className;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== content || $[28] !== props || $[29] !== ref || $[30] !== t8) {
    t9 = <div ref={ref} className={t8} {...props}>{content}</div>;
    $[27] = content;
    $[28] = props;
    $[29] = ref;
    $[30] = t8;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  return t9;
}
export function CardMetricGroupHeader(t0) {
  const $ = _c(11);
  const {
    label,
    value,
    href,
    isLoading: t1
  } = t0;
  const isLoading = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== label) {
    t2 = <div className="min-w-0 flex-1 truncate text-base font-medium leading-normal text-text-secondary">{label}</div>;
    $[0] = label;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== isLoading || $[3] !== value) {
    t3 = <div className="flex min-w-12 shrink-0 justify-end"><CardMetricCount value={value} tone="strong" isLoading={isLoading} /></div>;
    $[2] = isLoading;
    $[3] = value;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = <div className="flex min-h-10 w-full items-center gap-3 rounded-kbd bg-bg-primary px-3 py-2 hover:bg-fill-hover">{t2}{t3}</div>;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const content = t4;
  if (href) {
    let t5;
    if ($[8] !== content || $[9] !== href) {
      t5 = <Link href={href} prefetch={false} className="block w-full rounded-kbd">{content}</Link>;
      $[8] = content;
      $[9] = href;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    return t5;
  }
  return content;
}

// A labelled group of metric rows under its total, for stacked
// `CardContent divided={false}` bodies.
export function CardMetricGroup(t0) {
  const $ = _c(10);
  const {
    label,
    value,
    href,
    isLoading: t1,
    children
  } = t0;
  const isLoading = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== href || $[1] !== isLoading || $[2] !== label || $[3] !== value) {
    t2 = <CardMetricGroupHeader label={label} value={value} href={href} isLoading={isLoading} />;
    $[0] = href;
    $[1] = isLoading;
    $[2] = label;
    $[3] = value;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== children) {
    t3 = Children.count(children) > 0 && <div className="flex w-full flex-col">{children}</div>;
    $[5] = children;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <div className="flex w-full flex-col gap-1">{t2}{t3}</div>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
export function CardMetricRow(t0) {
  const $ = _c(18);
  const {
    label,
    value,
    href,
    tone: t1,
    isLoading: t2,
    wrapLabel: t3
  } = t0;
  const tone = t1 === undefined ? "quiet" : t1;
  const isLoading = t2 === undefined ? false : t2;
  const wrapLabel = t3 === undefined ? false : t3;
  const t4 = wrapLabel ? "break-words" : "truncate";
  const t5 = (value ?? 0) > 0 && tone !== "muted" ? "text-text-secondary" : "text-text-quaternary";
  let t6;
  if ($[0] !== t4 || $[1] !== t5) {
    t6 = cn("min-w-0 flex-1 text-base", t4, t5);
    $[0] = t4;
    $[1] = t5;
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  let t7;
  if ($[3] !== label || $[4] !== t6) {
    t7 = <div className={t6}>{label}</div>;
    $[3] = label;
    $[4] = t6;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] !== isLoading || $[7] !== tone || $[8] !== value) {
    t8 = <div className="flex min-w-12 shrink-0 justify-end"><CardMetricCount value={value} tone={tone} isLoading={isLoading} /></div>;
    $[6] = isLoading;
    $[7] = tone;
    $[8] = value;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== t7 || $[11] !== t8) {
    t9 = <div className="flex min-h-10 w-full items-center gap-3 px-3 py-2">{t7}{t8}</div>;
    $[10] = t7;
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const content = t9;
  if (href) {
    let t10;
    if ($[13] !== content || $[14] !== href) {
      t10 = <Link href={href} prefetch={false} className="block w-full rounded-none border-b border-border-subtle last:border-b-0 hover:bg-fill-hover-subtle">{content}</Link>;
      $[13] = content;
      $[14] = href;
      $[15] = t10;
    } else {
      t10 = $[15];
    }
    return t10;
  }
  let t10;
  if ($[16] !== content) {
    t10 = <div className="w-full rounded-none border-b border-border-subtle last:border-b-0">{content}</div>;
    $[16] = content;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  return t10;
}
export type CardMetricListItem = {
  key: string;
  label: string;
  value: number | undefined;
  href?: string;
  valueTone?: CardMetricLineValueTone;
};

/**
 * Renders a flat list of metric lines, hiding the empty (zero) rows behind a
 * "Show all" toggle so populated rows stay above the fold. While loading every
 * row is shown (values aren't known yet) so the skeletons don't jump.
 */
export function CardMetricList(t0) {
  const $ = _c(18);
  const {
    items,
    isLoading: t1
  } = t0;
  const isLoading = t1 === undefined ? false : t1;
  const [showEmpty, setShowEmpty] = useState(false);
  let t2;
  if ($[0] !== items) {
    t2 = items.filter(_temp);
    $[0] = items;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const emptyRows = t2.length;
  let t3;
  if ($[2] !== isLoading || $[3] !== items || $[4] !== showEmpty) {
    t3 = isLoading || showEmpty ? items : items.filter(_temp2);
    $[2] = isLoading;
    $[3] = items;
    $[4] = showEmpty;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const visibleItems = t3;
  let t4;
  if ($[6] !== isLoading || $[7] !== visibleItems) {
    let t5;
    if ($[9] !== isLoading) {
      t5 = item_1 => <CardMetricLine key={item_1.key} label={item_1.label} value={item_1.value} href={item_1.href} valueTone={item_1.valueTone} isLoading={isLoading} />;
      $[9] = isLoading;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    t4 = visibleItems.map(t5);
    $[6] = isLoading;
    $[7] = visibleItems;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[11] !== emptyRows || $[12] !== isLoading || $[13] !== showEmpty) {
    t5 = !isLoading && emptyRows > 0 && <CardItemButton tone="muted" onClick={() => setShowEmpty(_temp3)}><ChevronDown className={cn("size-3.5", showEmpty && "rotate-180")} aria-hidden="true" />{showEmpty ? "Hide empty" : `Show all (${emptyRows} empty)`}</CardItemButton>;
    $[11] = emptyRows;
    $[12] = isLoading;
    $[13] = showEmpty;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t4 || $[16] !== t5) {
    t6 = <>{t4}{t5}</>;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  return t6;
}
function _temp3(value) {
  return !value;
}
function _temp2(item_0) {
  return (item_0.value ?? 0) > 0;
}
function _temp(item) {
  return (item.value ?? 0) === 0;
}